import { FC, ReactElement, SyntheticEvent, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// Components
import MapleContainer from 'Components/ds/MapleContainer';
import Typography from 'Components/ds/Typography';
import TypographyComplex from 'Components/ds/TypographyComplex';
import MapleLink from 'Components/ds/MapleLink';
import { SectionHeader, Question } from 'Pages/PoolsV2Transition';

const FAQ: FC = (): ReactElement => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Box sx={{ backgroundColor: 'ds.background.primary' }}>
      <MapleContainer maxWidth='xl' sx={{ py: 10 }}>
        <Grid container spacing={5} justifyContent='center'>
          <SectionHeader
            subtitle='F.A.Q.'
            title='Frequently Asked Questions'
            text='Answers to common questions are provided below. If you have further questions please reach out via Intercom or to your Account Manager.'
          />
          <Grid item xs={12} md={8}>
            <Question
              expanded={expanded === 'q1'}
              onChange={handleChange('q1')}
              question='Why will the app be unavailable during the upgrade?'
              answer={
                <Stack direction='column' spacing={2}>
                  <Typography color='ds.neutral.400' variant='paragraphSmall'>
                    The app will be unavailable to ensure a smooth transition to Maple 2.0 smart contracts. We are
                    controlling the migration so that users do not have to take any action and so everyone can benefit
                    from the protocol changes at launch.
                  </Typography>
                  <MapleLink
                    href='https://twitter.com/maplefinance'
                    target='_blank'
                    rel='noreferrer'
                    link='info'
                    variant='paragraphSmall'
                  >
                    {'Maple Twitter Page for Live Updates >'}
                  </MapleLink>
                </Stack>
              }
            />
            <Question
              expanded={expanded === 'q2'}
              onChange={handleChange('q2')}
              question='Will I be able to make withdrawals during the upgrade?'
              answer={
                <Stack direction='column' spacing={2}>
                  <Typography color='ds.neutral.400' variant='paragraphSmall'>
                    In the instance that your withdrawal window opens while the app is unavailable, your lending
                    position will not be jeopardized and you will continue to earn interest on your position. Delegates
                    will open withdrawal windows within 14 days of the launch of Maple 2.0 and you will then be able to
                    schedule your withdrawal and access your funds within the same timeframe.
                  </Typography>
                  <Typography color='ds.neutral.400' variant='paragraphSmall'>
                    If you have returned to Maple to withdraw your position and the app is unavailable, you will not be
                    able to request to withdraw during this time. Delegates will open withdrawal windows within 14 days
                    of the launch of Maple 2.0, and you will then be able to schedule your withdrawal. Your lending
                    position will continue to earn interest during this time.
                  </Typography>
                  <MapleLink
                    href='https://twitter.com/maplefinance'
                    target='_blank'
                    rel='noreferrer'
                    link='info'
                    variant='paragraphSmall'
                  >
                    {'Maple Twitter Page for Live Updates >'}
                  </MapleLink>
                </Stack>
              }
            />
            <Question
              expanded={expanded === 'q3'}
              onChange={handleChange('q3')}
              question='How does this affect me as a lender into the M11 Credit pools?'
              answer={
                <Stack direction='column' spacing={2}>
                  <Typography color='ds.neutral.400' variant='paragraphSmall'>
                    The recent default by Orthogonal Trading, has made clear the immediate need for features in the
                    Maple 2.0 upgrade -- Specifically the need to declare early default on a loan when it is clear the
                    borrower will not be able to repay.
                  </Typography>
                  <Typography color='ds.neutral.600' variant='paragraphSmall' weight='bold'>
                    To ensure the losses of the Orthogonal default are fairly distributed we will be declaring early
                    default on all Orthogonal loans the moment Maple has upgraded to Maple 2.0.
                  </Typography>
                  <Typography color='ds.neutral.400' variant='paragraphSmall'>
                    The Maple team has been working on this upgrade for the past year. By focusing on user feedback, we
                    have developed features that allow us to better handle defaults. The team will continue to place an
                    emphasis on using user feedback as a central building block when developing new features.
                  </Typography>
                </Stack>
              }
            />
            <Question
              expanded={expanded === 'q4'}
              onChange={handleChange('q4')}
              question='Has Maple 2.0 been audited?'
              answer='Yes. Protocol security is a top priority and the team has run over 1000 tests and simulations with the new smart contracts. The new contracts have been audited by 3 leading auditors - Trail of Bits, Spearbit and Three Sigma and the audits will be released post launch. Maple will release the code post launch and will significantly increase the scope of the always-on bug bounty program with Immunefi.'
            />
            <Question
              expanded={expanded === 'q5'}
              onChange={handleChange('q5')}
              question='Do I need to do anything?'
              answer='No. The transition from 1.0 to 2.0 has been methodically planned and customers do not need to take any action. During the upgrade Maple 1.0 LP Tokens will be retired and replaced with Maple 2.0 LP Tokens. Maple 2.0 LP Tokens will be sent directly to your wallet during the upgrade.'
            />
            <Question
              expanded={expanded === 'q6'}
              onChange={handleChange('q6')}
              question='Are funds earning MPL rewards through staking affected?'
              answer={
                <TypographyComplex
                  align='left'
                  color='ds.neutral.400'
                  variant='paragraphSmall'
                  globalWeight='regular'
                  items={[
                    {
                      copy: 'In August, we moved to',
                      type: 'copy',
                    },
                    {
                      copy: 'distributing rewards',
                      type: 'link',
                      url: 'https://intercom.help/maplefinance/en/articles/6404750-changes-to-mpl-lending-rewards',
                    },
                    {
                      copy: 'directly to wallets to simplify transactions for users. In November we discontinued the MPL rewards programme, as part of a continued tapering and so to allow the underlying yield from borrower repayments to be the main source of yield on the Maple protocol.  The upgrade will not affect rewards earned in this way. If you have MPL rewards that have yet to be claimed or BPT’s in cover, post upgrade the Maple 1.0 UI will be made available to claim unclaimed MPL rewards or BPTs.',
                      type: 'copy',
                    },
                  ]}
                />
              }
            />
            <Question
              expanded={expanded === 'q7'}
              onChange={handleChange('q7')}
              question='What if I am integrated with Maple?'
              answer='Please reach out to the team as soon as possible via Intercom (Messenger is located on the bottom right hand corner) or email (linked in the contact us section above the footer)'
            />
            <Question
              expanded={expanded === 'q8'}
              onChange={handleChange('q8')}
              question='Is xMPL affected?'
              answer='Access to the UI will not be possible but the xMPL contract will be functional.'
            />
          </Grid>
        </Grid>
      </MapleContainer>
    </Box>
  );
};

export default FAQ;
